import * as React from 'react';
import {
  Award,
  Edit3,
  Loader2,
  UploadCloud,
  X,
  Plus,
  Trash2,
  Trophy,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginationBar } from 'core/components/PaginationBar';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import {
  recognitionsAchievementsApi,
  RecognitionAchievementItem,
  RecognitionAchievementTab,
} from 'features/DigitalMarketing/pages/EditWebsiteContent/api/recognitionsAchievementsApi';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMText,
} from '../../../components/Primitives';

const RecognitionAchievementCardSkeleton = () => (
  <DMDiv
    as="article"
    className="mx-[1vw] my-[0.6vh] flex min-h-[12vh] shrink-0 animate-pulse items-center justify-between gap-[2vw] rounded-[8px] bg-white px-[2.4vw] py-[2vh] shadow-[0_4px_18px_rgba(0,0,0,0.16)]"
  >
    <DMDiv className="flex min-w-0 flex-1 items-center gap-[2.2vw]">
      <DMDiv className="h-[7vh] w-[5.2vw] shrink-0 rounded-[8px] bg-gray-200" />
      <DMDiv className="min-w-0 flex-1">
        <DMDiv className="h-[2.4vh] w-[38%] rounded bg-gray-200" />
        <DMDiv className="mt-[1.3vh] flex items-center gap-[0.6vw]">
          <DMDiv className="h-[2.5vh] w-[8vw] rounded-full bg-gray-200" />
          <DMDiv className="h-[2.5vh] w-[6vw] rounded-full bg-gray-200" />
        </DMDiv>
      </DMDiv>
    </DMDiv>
    <DMDiv className="flex shrink-0 overflow-hidden rounded-[6px] border border-gray-200 bg-white">
      <DMDiv className="h-[4vh] w-[4.8vw] border-r border-gray-200 bg-gray-100" />
      <DMDiv className="h-[4vh] w-[5.2vw] bg-gray-100" />
    </DMDiv>
  </DMDiv>
);

const RecognitionAchievementDetailsModal = ({
  item,
  isAchievement,
  onClose,
}: {
  item: RecognitionAchievementItem | null;
  isAchievement: boolean;
  onClose: () => void;
}) => {
  if (!item) {
    return null;
  }

  return (
    <DMDiv className="fixed inset-0 z-[10002] flex items-center justify-center px-[3vw] py-[4vh]">
      <DMButton
        type="button"
        className="absolute inset-0 bg-black/75"
        onClick={onClose}
        aria-label="Close details backdrop"
      />

      <DMDiv
        as="section"
        className="relative z-10 flex max-h-[88vh] w-[58vw] max-w-[980px] flex-col overflow-hidden rounded-[10px] bg-white shadow-[0_18px_45px_rgba(0,0,0,0.28)]"
      >
        <DMDiv className="flex shrink-0 items-start justify-between gap-[2vw] border-b border-gray-200 px-[2vw] py-[2.2vh]">
          <DMDiv className="min-w-0">
            <DMText className="text-[1.45vh] font-semibold uppercase leading-none text-primary-main">
              {isAchievement ? 'Achievement' : 'Recognition'}
            </DMText>
            <DMText
              as="h2"
              className="mt-[0.9vh] truncate text-[3vh] font-bold leading-tight text-gray-950"
            >
              {item.title || '-'}
            </DMText>
          </DMDiv>
          <DMButton
            type="button"
            onClick={onClose}
            className="flex h-[4.4vh] w-[4.4vh] shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label="Close details"
          >
            <X className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.2} />
          </DMButton>
        </DMDiv>

        <DMDiv className="min-h-0 overflow-y-auto px-[2vw] py-[2.2vh]">
          <DMDiv className="grid grid-cols-[0.8fr_1.2fr] gap-[2vw]">
            <DMDiv className="min-h-[34vh] overflow-hidden rounded-[8px] bg-primary-10">
              {item.image ? (
                <DMImage
                  src={item.image}
                  alt={item.title || item.category}
                  className="h-full w-full object-cover"
                />
              ) : (
                <DMDiv className="flex h-full min-h-[34vh] items-center justify-center">
                  {isAchievement ? (
                    <Trophy
                      className="h-[10vh] w-[10vh] fill-amber-400 text-amber-500"
                      strokeWidth={1.8}
                    />
                  ) : (
                    <Award
                      className="h-[10vh] w-[10vh] fill-amber-400 text-amber-500"
                      strokeWidth={1.8}
                    />
                  )}
                </DMDiv>
              )}
            </DMDiv>

            <DMDiv className="min-w-0">
              <DMDiv className="flex flex-wrap items-center gap-[0.7vw]">
                <DMText
                  as="span"
                  className="rounded-full bg-[#ead8ff] px-[0.9vw] py-[0.55vh] text-[1.55vh] font-semibold leading-none text-[#6d28b4]"
                >
                  {item.issuedBy || item.category}
                </DMText>
                <DMText
                  as="span"
                  className="inline-flex shrink-0 items-center gap-[0.4vw] whitespace-nowrap rounded-full bg-[#d8ffe8] px-[0.9vw] py-[0.55vh] text-[1.55vh] font-semibold leading-none text-[#1fa463]"
                >
                  <DMText
                    as="span"
                    className="h-[0.8vh] w-[0.8vh] rounded-full bg-[#18c267]"
                  />
                  <DMText as="span">{item.year || '-'}</DMText>
                </DMText>
              </DMDiv>

              <DMDiv className="mt-[2.8vh]">
                <DMText
                  as="h3"
                  className="text-[1.5vh] font-semibold uppercase leading-none text-gray-500"
                >
                  Issued By
                </DMText>
                <DMText className="mt-[0.9vh] text-[1.85vh] font-medium leading-snug text-gray-800">
                  {item.issuedBy || '-'}
                </DMText>
              </DMDiv>

              <DMDiv className="mt-[2.4vh]">
                <DMText
                  as="h3"
                  className="text-[1.5vh] font-semibold uppercase leading-none text-gray-500"
                >
                  Issued Year
                </DMText>
                <DMText className="mt-[0.9vh] text-[1.85vh] font-medium leading-snug text-gray-800">
                  {item.year || '-'}
                </DMText>
              </DMDiv>

              <DMDiv className="mt-[2.4vh] grid grid-cols-2 gap-[1.5vw]">
                <DMDiv>
                  <DMText
                    as="h3"
                    className="text-[1.5vh] font-semibold uppercase leading-none text-gray-500"
                  >
                    Issued For
                  </DMText>
                  <DMText className="mt-[0.9vh] text-[1.85vh] font-medium leading-snug text-gray-800">
                    {item.issuedFor || '-'}
                  </DMText>
                </DMDiv>
                <DMDiv>
                  <DMText
                    as="h3"
                    className="text-[1.5vh] font-semibold uppercase leading-none text-gray-500"
                  >
                    Issued Level
                  </DMText>
                  <DMText className="mt-[0.9vh] text-[1.85vh] font-medium leading-snug text-gray-800">
                    {item.issuedLevel || '-'}
                  </DMText>
                </DMDiv>
              </DMDiv>

              <DMDiv className="mt-[2.4vh]">
                <DMText
                  as="h3"
                  className="text-[1.5vh] font-semibold uppercase leading-none text-gray-500"
                >
                  Description
                </DMText>
                <DMText className="mt-[1vh] whitespace-pre-wrap text-[1.85vh] font-medium leading-relaxed text-gray-800">
                  {item.description || '-'}
                </DMText>
              </DMDiv>
            </DMDiv>
          </DMDiv>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

export function RecognitionsAchievements() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] =
    React.useState<RecognitionAchievementTab>('achievements');
  const [achievementRows, setAchievementRows] = React.useState<
    RecognitionAchievementItem[]
  >([]);
  const [recognitionRows, setRecognitionRows] = React.useState<
    RecognitionAchievementItem[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [deleteItem, setDeleteItem] =
    React.useState<RecognitionAchievementItem | null>(null);
  const [deletingItemId, setDeletingItemId] = React.useState<number | null>(
    null
  );
  const [publishingItemId, setPublishingItemId] = React.useState<number | null>(
    null
  );
  const [selectedItem, setSelectedItem] =
    React.useState<RecognitionAchievementItem | null>(null);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const requestIdRef = React.useRef(0);

  const isAchievementsTab = activeTab === 'achievements';
  const rows = isAchievementsTab ? achievementRows : recognitionRows;
  const totalRecords = rows.length;
  const paginatedRows = React.useMemo(() => {
    const startIndex = (pageIndex - 1) * pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [pageIndex, pageSize, rows]);
  const addLabel = isAchievementsTab ? 'Add Achievement' : 'Add Recognition';
  const createPath = isAchievementsTab
    ? buildEditWebsiteContentPath(
        'recognitions-achievements/create-achievement'
      )
    : buildEditWebsiteContentPath(
        'recognitions-achievements/create-recognition'
      );

  const handlePageSizeChange = React.useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  }, []);

  const fetchRows = React.useCallback(async (currentPageSize: number) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);

    try {
      const items = await recognitionsAchievementsApi.list(currentPageSize);

      setAchievementRows(items.filter(item => item.category === 'ACHIEVEMENT'));
      setRecognitionRows(items.filter(item => item.category === 'RECOGNITION'));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(
          error,
          'Unable to fetch recognitions and achievements'
        ),
        'error'
      );
      setAchievementRows([]);
      setRecognitionRows([]);
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    fetchRows(pageSize);
  }, [fetchRows, pageSize]);

  React.useEffect(() => {
    setSelectedItem(null);
    setPageIndex(1);
  }, [activeTab]);

  React.useEffect(() => {
    const maxPageIndex = Math.max(1, Math.ceil(totalRecords / pageSize));
    if (pageIndex > maxPageIndex) {
      setPageIndex(maxPageIndex);
    }
  }, [pageIndex, pageSize, totalRecords]);

  const handleEdit = (item: RecognitionAchievementItem) => {
    navigate(
      isAchievementsTab
        ? buildEditWebsiteContentPath(
            'recognitions-achievements/edit-achievement'
          )
        : buildEditWebsiteContentPath(
            'recognitions-achievements/edit-recognition'
          ),
      { state: { item, mode: activeTab } }
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) {
      return;
    }

    setDeletingItemId(deleteItem.id);

    try {
      const response = await recognitionsAchievementsApi.delete(
        deleteItem.recognitionsAchievementsId || deleteItem.id
      );
      setDeleteItem(null);
      showGlobalModal(
        response?.data?.message ||
          `${
            isAchievementsTab ? 'Achievement' : 'Recognition'
          } deleted successfully`
      );
      fetchRows(pageSize);
    } catch (error: any) {
      setDeleteItem(null);
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete item'),
        'error'
      );
    } finally {
      setDeletingItemId(null);
    }
  };

  const handlePublishToggle = async (item: RecognitionAchievementItem) => {
    setPublishingItemId(item.id);

    try {
      const shouldPublish = !item.isPublished;
      const response = await recognitionsAchievementsApi.publish(
        item.recognitionsAchievementsId || item.id,
        shouldPublish
      );

      showGlobalModal(
        response?.data?.message ||
          (shouldPublish
            ? 'Published successfully'
            : 'Unpublished successfully')
      );
      fetchRows(pageSize);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to update publish status'),
        'error'
      );
    } finally {
      setPublishingItemId(null);
    }
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[2.4vh] shrink-0">
        <DigitalMarketingBreadcrumb
          className="mb-[1.6vh]"
          items={[
            {
              label: 'Edit Website Content',
              path: EDIT_WEBSITE_CONTENT_BASE_PATH,
            },
            { label: 'Recognitions & Achievements' },
          ]}
        />

        <DMText
          as="h1"
          className="text-[3.9vh] font-bold leading-tight text-gray-950"
        >
          Recognitions & Achievements
        </DMText>
        <DMText className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
          Show your success. Build credibility. Earn Parents trust
        </DMText>
      </DMDiv>

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col rounded-[18px] border border-gray-400 bg-[#f8f9fb] px-[1.5vw] py-[2.4vh]"
      >
        <DMDiv className="mb-[2.4vh] flex shrink-0 items-center justify-between gap-[2vw]">
          <DMDiv className="flex h-[7vh] items-center rounded-full border border-gray-300 bg-white p-[0.2vh]">
            <DMButton
              type="button"
              onClick={() => {
                setActiveTab('achievements');
              }}
              className={`flex h-[5.8vh] min-w-[13.8vw] items-center justify-center gap-[0.6vw] rounded-full px-[1.5vw] text-[1.75vh] font-medium transition-colors ${
                isAchievementsTab
                  ? 'bg-primary-main text-white'
                  : 'bg-white text-primary-main hover:bg-primary-10'
              }`}
            >
              <Trophy className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
              <DMText as="span">Achievements</DMText>
            </DMButton>
            <DMButton
              type="button"
              onClick={() => {
                setActiveTab('recognitions');
              }}
              className={`flex h-[5.8vh] min-w-[13.1vw] items-center justify-center gap-[0.6vw] rounded-full px-[1.5vw] text-[1.75vh] font-medium transition-colors ${
                !isAchievementsTab
                  ? 'bg-primary-main text-white'
                  : 'bg-white text-primary-main hover:bg-primary-10'
              }`}
            >
              <Award className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
              <DMText as="span">Recognitions</DMText>
            </DMButton>
          </DMDiv>

          <DMButton
            type="button"
            onClick={() => navigate(createPath)}
            className="flex h-[7vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.75vh] font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
            <DMText as="span">{addLabel}</DMText>
          </DMButton>
        </DMDiv>

        <DMDiv className="flex min-h-0 flex-1 flex-col gap-[1vh] overflow-y-auto">
          {isLoading && (
            <>
              {Array.from({ length: 4 }).map((_, index) => (
                <RecognitionAchievementCardSkeleton key={index} />
              ))}
            </>
          )}

          {!isLoading && !rows.length && (
            <DMDiv className="flex h-[32vh] items-center justify-center rounded-[12px] border border-dashed border-gray-300 bg-white text-[1.8vh] font-medium text-gray-500">
              No {isAchievementsTab ? 'achievements' : 'recognitions'} found
            </DMDiv>
          )}

          {!isLoading &&
            paginatedRows.map(item => (
              <DMDiv
                as="article"
                key={`${activeTab}-${item.id}`}
                onClick={() => setSelectedItem(item)}
                className="mx-[1vw] my-[0.6vh] flex min-h-[12vh] shrink-0 cursor-pointer items-center justify-between gap-[2vw] rounded-[8px] bg-white px-[2.4vw] py-[2vh] shadow-[0_4px_18px_rgba(0,0,0,0.16)] transition-shadow hover:shadow-[0_6px_22px_rgba(0,0,0,0.2)]"
              >
                <DMDiv className="flex min-w-0 items-center gap-[2.2vw]">
                  <DMDiv className="flex h-[7vh] w-[5.2vw] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-primary-10">
                    {item.image ? (
                      <DMImage
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    ) : isAchievementsTab ? (
                      <Trophy
                        className="h-[5vh] w-[5vh] fill-amber-400 text-amber-500"
                        strokeWidth={1.8}
                      />
                    ) : (
                      <Award
                        className="h-[5vh] w-[5vh] fill-amber-400 text-amber-500"
                        strokeWidth={1.8}
                      />
                    )}
                  </DMDiv>

                  <DMDiv className="min-w-0">
                    <DMText
                      as="h2"
                      className="truncate text-[2.3vh] font-bold leading-tight text-black"
                    >
                      {item.title || '-'}
                    </DMText>
                    <DMDiv className="mt-[1.3vh] flex flex-wrap items-center gap-[0.6vw]">
                      <DMText
                        as="span"
                        className="rounded-full bg-[#ead8ff] px-[0.9vw] py-[0.55vh] text-[1.55vh] font-semibold leading-none text-[#6d28b4]"
                      >
                        {item.issuedBy || item.category}
                      </DMText>
                      <DMText
                        as="span"
                        className="inline-flex shrink-0 items-center gap-[0.4vw] whitespace-nowrap rounded-full bg-[#d8ffe8] px-[0.9vw] py-[0.55vh] text-[1.55vh] font-semibold leading-none text-[#1fa463]"
                      >
                        <DMText
                          as="span"
                          className="h-[0.8vh] w-[0.8vh] rounded-full bg-[#18c267]"
                        />
                        <DMText as="span">{item.year || '-'}</DMText>
                      </DMText>
                      <DMText
                        as="span"
                        className={`rounded-full px-[0.9vw] py-[0.55vh] text-[1.55vh] font-semibold leading-none ${
                          item.isPublished
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-amber-100 text-amber-600'
                        }`}
                      >
                        {item.isPublished ? 'Published' : 'Unpublished'}
                      </DMText>
                    </DMDiv>
                  </DMDiv>
                </DMDiv>

                <DMDiv className="flex shrink-0 overflow-hidden rounded-[6px] border border-gray-300 bg-white">
                  <DMButton
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      handleEdit(item);
                    }}
                    className="flex h-[4vh] items-center gap-[0.35vw] border-r border-gray-300 px-[0.75vw] text-[1.35vh] font-semibold text-purple-700 transition-colors hover:bg-purple-50"
                  >
                    <Edit3 className="h-[1.7vh] w-[1.7vh]" strokeWidth={2} />
                    <DMText as="span">Edit</DMText>
                  </DMButton>
                  <DMButton
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      handlePublishToggle(item);
                    }}
                    className="flex h-[4vh] items-center gap-[0.35vw] border-r border-gray-300 px-[0.75vw] text-[1.35vh] font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
                  >
                    {publishingItemId === item.id ? (
                      <Loader2
                        className="h-[1.7vh] w-[1.7vh] animate-spin"
                        strokeWidth={2}
                      />
                    ) : (
                      <UploadCloud
                        className="h-[1.7vh] w-[1.7vh]"
                        strokeWidth={2}
                      />
                    )}
                    <DMText as="span">
                      {item.isPublished ? 'Unpublish' : 'Publish'}
                    </DMText>
                  </DMButton>
                  <DMButton
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      setDeleteItem(item);
                    }}
                    className="flex h-[4vh] items-center gap-[0.35vw] px-[0.75vw] text-[1.35vh] font-semibold text-red-500 transition-colors hover:bg-red-50"
                  >
                    {deletingItemId === item.id ? (
                      <Loader2
                        className="h-[1.7vh] w-[1.7vh] animate-spin"
                        strokeWidth={2}
                      />
                    ) : (
                      <Trash2 className="h-[1.7vh] w-[1.7vh]" strokeWidth={2} />
                    )}
                    <DMText as="span">Delete</DMText>
                  </DMButton>
                </DMDiv>
              </DMDiv>
            ))}
        </DMDiv>

        {(isLoading || totalRecords > 0) && (
          <DMDiv className="mt-[2vh] shrink-0 [&>div]:bg-transparent">
            <PaginationBar
              key={`${activeTab}-${pageSize}`}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPageIndex}
              onPageSizeChange={handlePageSizeChange}
              itemName={isAchievementsTab ? 'achievements' : 'recognitions'}
            />
          </DMDiv>
        )}
      </DMDiv>

      <DigitalMarketingDeleteModal
        isOpen={!!deleteItem}
        title={`Are you sure you want to delete this ${
          isAchievementsTab ? 'achievement' : 'recognition'
        }?`}
        isDeleting={!!deleteItem && deletingItemId === deleteItem.id}
        onCancel={() => {
          setDeleteItem(null);
          setDeletingItemId(null);
        }}
        onConfirm={handleConfirmDelete}
      />
      <RecognitionAchievementDetailsModal
        item={selectedItem}
        isAchievement={isAchievementsTab}
        onClose={() => setSelectedItem(null)}
      />
    </DMDiv>
  );
}
